
/**
 * Generates structured data for a video
 */
export const generateVideoStructuredData = (
  title: string,
  description: string,
  videoUrl: string,
  thumbnailUrl: string,
  uploadDate: string,
  duration?: string,
  contentUrl?: string,
  embedUrl?: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": title,
    "description": description,
    "thumbnailUrl": thumbnailUrl,
    "uploadDate": uploadDate,
    ...(duration && { "duration": duration }),
    ...(contentUrl && { "contentUrl": contentUrl }),
    ...(embedUrl && { "embedUrl": embedUrl })
  };
};

/**
 * Generates structured data for an article
 */
export const generateArticleStructuredData = (
  headline: string,
  description: string,
  authorName: string,
  publisherName: string,
  publisherLogoUrl: string,
  imageUrl: string,
  datePublished: string,
  dateModified?: string
) => {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "description": description,
    "image": imageUrl,
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": "Person",
      "name": authorName
    },
    "publisher": {
      "@type": "Organization",
      "name": publisherName,
      "logo": {
        "@type": "ImageObject",
        "url": publisherLogoUrl
      }
    }
  };
};

/**
 * Generates structured data for a product
 */
export const generateProductStructuredData = (
  name: string,
  description: string,
  imageUrl: string,
  price?: number,
  priceCurrency?: string,
  ratingValue?: number,
  reviewCount?: number
) => {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "image": imageUrl,
    ...(price && priceCurrency && {
      "offers": {
        "@type": "Offer",
        "price": price.toString(),
        "priceCurrency": priceCurrency
      }
    }),
    ...(ratingValue && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": ratingValue.toString(),
        "reviewCount": (reviewCount || 1).toString()
      }
    })
  };
};

/**
 * Generates structured data for FAQ
 */
export const generateFAQStructuredData = (
  questions: Array<{ question: string; answer: string }>
) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(q => ({
      "@type": "Question",
      "name": q.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": q.answer
      }
    }))
  };
};

/**
 * Generates breadcrumb structured data
 */
export const generateBreadcrumbStructuredData = (
  breadcrumbs: Array<{ name: string; url: string }>
) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  };
};
